import {injectable} from 'inversify'
import {action, computed, makeObservable, observable, reaction, runInAction, toJS} from 'mobx'
import {subscribe} from 'dna-react-ioc'

import {EpaySummary, getSettlementsSummary, SettlementsStatuses, SummaryItem} from '@/stores'
import {
    AvailableStoresDataStore,
    AvailableStoresDataStoreSymbol,
    EcomStoreSelectStore
} from '@/stores/store-and-terminals'
import {
    TRangePickerContainerStore,
    TSelectContainerStore,
    TTransactionSummaryContainerStore
} from '@/components/containers'
import {Currencies, SERVER_DATE_FORMAT, SettlementsStatus} from '@/constants'
import {DEFAULT_CURRENCY} from '@/constants/currencies'
import {LoadingState} from '@/types'
import {openErrorNotification, deepCopy} from '@/utils'
import {SettlementsSummary} from '@/stores/reports/models/SettlementsSummary'
import {calculatePercent, findItemByStatus} from '@/stores/reports/services/utills'

@injectable()
export class SettlementsTransactionSummaryStore implements TTransactionSummaryContainerStore {
    private readonly _rangePickerStore: TRangePickerContainerStore
    private readonly _currencySelectStore: TSelectContainerStore
    private readonly _merchantStoresStore: AvailableStoresDataStore
    private _storeSelectStore: EcomStoreSelectStore

    constructor(
        protected _name: string,
        rangePickerStore: TRangePickerContainerStore,
        currencySelectStore: TSelectContainerStore,
        storeSelectStore?: EcomStoreSelectStore
    ) {
        this._rangePickerStore = rangePickerStore
        this._currencySelectStore = currencySelectStore
        this._storeSelectStore = storeSelectStore
        this._merchantStoresStore = subscribe<AvailableStoresDataStore>(
            AvailableStoresDataStoreSymbol
        )

        makeObservable(this, {
            loadingState: observable,
            prevSummary: observable,
            currSummary: observable,

            summaryByStatus: computed,
            currSummaryRequest: computed,
            prevDates: computed,
            currency: computed,
            loadSummary: action.bound
        })

        reaction(
            () => ({
                from: this._rangePickerStore.startDate,
                to: this._rangePickerStore.endDate,
                currency: this._currencySelectStore.value
            }),
            ({from, to, currency}) => {
                if (from && to && currency) {
                    this.loadSummary()
                }
            }
        )

        this._currencySelectStore.setValue(DEFAULT_CURRENCY)
        this.loadSummary()
    }

    loadingState = LoadingState.IDLE
    prevSummary: EpaySummary = {currency: null, data: []}
    currSummary: EpaySummary = {currency: null, data: []}
    private _abortController: AbortController | null = null

    get summaryByStatus() {
        const statuses = SettlementsStatuses.map((status) => {
            const currItem = deepCopy(findItemByStatus(this.currSummary.data, status))
            const prevItem = deepCopy(findItemByStatus(this.prevSummary.data, status))

            if (!currItem)
                return {
                    count: 0,
                    amount: 0,
                    percent: 0
                }

            const percent = calculatePercent(currItem.amount || 0, prevItem?.amount || 0)

            return {
                ...currItem,
                percent
            }
        })

        return statuses
    }

    get currSummaryRequest() {
        return {
            from: this._rangePickerStore.startDate.format(SERVER_DATE_FORMAT),
            to: this._rangePickerStore.endDate.format(SERVER_DATE_FORMAT),
            currency: this._currencySelectStore.value
        }
    }

    get prevDates() {
        return {
            from: this._rangePickerStore.prevDates.startDate,
            to: this._rangePickerStore.prevDates.endDate
        }
    }

    get currency() {
        return this._currencySelectStore.value as Currencies
    }

    public async loadSummary() {
        this._abortController?.abort()
        const abortController = new AbortController()
        this._abortController = abortController

        const formatApiData = (data: SettlementsSummary[]) => {
            const emptySummaryItems: SummaryItem[] = Object.values(SettlementsStatus).map(
                (status) => ({
                    status,
                    amount: 0,
                    count: 0
                })
            )

            if (!data.length)
                return {
                    currency: this._currencySelectStore.value,
                    data: emptySummaryItems
                }

            return {
                currency: this._currencySelectStore.value,
                data: data.map((item) => ({
                    amount: item.amount,
                    status: item.type,
                    count: item.count
                }))
            }
        }

        runInAction(() => {
            this.loadingState = LoadingState.LOADING
        })

        try {
            const {result, error} = await getSettlementsSummary(
                this.currSummaryRequest,
                abortController.signal
            )
            if (!result) throw new Error(error.message)

            if (this._abortController !== abortController) {
                return
            }

            runInAction(() => {
                this.currSummary = formatApiData(result)
                this.loadingState = LoadingState.DONE
            })
        } catch (err) {
            if (err?.name === 'AbortError') return

            if (this._abortController !== abortController) {
                return
            }

            this.loadingState = LoadingState.FAILED
            return openErrorNotification(err.message)
        }
    }
}
